import {
	useCallback, useMemo, useState,
} from 'react';

import {
	useIndustryChildrenQuery,
	useIndustryRootsQuery,
} from '@/modules/shared/application';

import type { NestedCategory } from '../composites/NestedCheckbox/NestedCheckbox';

/**
 * Custom hook for job industries selector state
 * Manages hierarchical industry selection with root and children levels
 */
export const useJobIndustriesSelectorState = () => {
	const [ group, setGroup ] = useState<null | string>(null);

	// Fetch root industries for sidebar categories
	const { data: rootIndustries = [] } = useIndustryRootsQuery();

	// Fetch children when a group is selected
	const { data: childIndustries, isLoading } = useIndustryChildrenQuery(
		group ?? undefined,
		{ enabled: !!group },
	);

	const [ selectedIds, setSelectedIds ] = useState<string[]>([]);

	// Map root industries to sidebar categories
	const categories = useMemo(
		() => rootIndustries.map(industry => ({
			id: industry.id,

			label: industry.localized.en,
			value: industry.id,
		})),
		[ rootIndustries ],
	);

	const selectedCategory = useMemo(
		() => categories.find(c => c.value === group),
		[ group, categories ],
	);

	const collections = useMemo<NestedCategory[]>(
		() => {
			if (!group || !selectedCategory || !childIndustries?.length) {
			// Return flat list when no group selected
				return (childIndustries ?? []).map(industry => ({
					id: industry.id,

					children: [],
					label:    industry.localized.en,
				}));
			}

			// Group industries by their direct parent (subcategory)
			// Chain contains: [subcategory] for leaf items
			const subcategoryMap = new Map<string, { items: typeof childIndustries; name: string }>();

			childIndustries.forEach((industry) => {
			// The chain[0] is the subcategory (parent of this leaf item)
				const subcategoryChain = industry.chain[0];

				if (!subcategoryChain) {
				// No subcategory in chain - it's a direct child of root
				// Add it to its own "subcategory"
					const subcategoryId = industry.id;
					const subcategoryName = industry.localized.en;

					if (!subcategoryMap.has(subcategoryId)) {
						subcategoryMap.set(subcategoryId, {
							items: [],
							name:  subcategoryName,
						});
					}

					return;
				}

				const subcategoryId = subcategoryChain.id;
				const subcategoryName = subcategoryChain.localized.en;

				if (!subcategoryMap.has(subcategoryId)) {
					subcategoryMap.set(subcategoryId, {
						items: [],
						name:  subcategoryName,
					});
				}

				subcategoryMap.get(subcategoryId)!.items.push(industry);
			});

			// Build nested structure: Root > Subcategories > Specialized industries
			const subcategories: NestedCategory[] = Array.from(subcategoryMap.entries()).map(([ subcategoryId, data ]) => ({
				id: subcategoryId,

				label: data.name,

				children: data.items.map(industry => ({
					id: industry.id,

					children: [],
					label:    industry.localized.en,
				})),
			}));

			// Always return with root category as parent
			return [
				{
					id: String(group),

					children: subcategories,
					label:    selectedCategory.label,
				},
			];
		},
		[ childIndustries, group, selectedCategory ],
	);

	const flattenIds = useCallback((n: NestedCategory): string[] =>
		// @ts-expect-error fix later
		[ n.id, ...(n.children?.flatMap(flattenIds) ?? []) ], []);

	const handleToggle = useCallback((id: string, checked: boolean) => {
		const findNode = (nodes: NestedCategory[]): NestedCategory | undefined =>
			// @ts-expect-error fix later
			nodes.find(n => n.id === id) || nodes.flatMap(n => n.children ?? []).find((n: NestedCategory) => n.id === id);

		const target = findNode(collections);

		if (target?.children?.length) {
			const ids = flattenIds(target).filter(x => x !== target.id);
			setSelectedIds((prev) => {
				const set = new Set(prev);

				if (checked) ids.forEach(x => set.add(x));
				else ids.forEach(x => set.delete(x));

				return Array.from(set);
			});

			return;
		}

		setSelectedIds(prev => (checked ? Array.from(new Set([ id, ...prev ])) : prev.filter(x => x !== id)));
	}, [ collections, flattenIds ]);

	return {
		categories,
		collections,
		group,
		handleToggle,
		isLoading,
		selectedIds,
		setGroup,
	};
};
